import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAutoLabels } from '../../../services/api';
import { useTranslation } from '../../../context/LanguageContext';
import './AutoLabelNew.css';

/* ===== 품질·생산 > 자동라벨링 =====
 * Auto Labeling Pipeline(AI 서비스)이 샷마다 붙인 자동 라벨과 신뢰도를 보여준다.
 *   라벨: 공정·비전·융합 중 하나라도 불량이면 불량 (특허 10-2025-0117160)
 *   신뢰도: 3시그마 정상 범위 근거 · 검사 간 일치 · 판정 여유 · 짝 신뢰도 · 연속 추세
 *   ≥ 0.85 자동 확정 → 학습셋,  < 0.85 작업자 검토 (AI 분석 > 라벨 검토)
 * 데이터: /machine/auto-label/{id}?date=&status=&page=&size=   스타일은 al- 접두어. */

const PAGE_SIZE = 15;
const REFRESH_MS = 30000;
const STATUS_FILTERS = ['', 'AUTO_CONFIRMED', 'REVIEW', 'AUDIT', 'WORKER_CONFIRMED', 'MISMATCH', 'NG'];
const TYPES = ['FLASH', 'WELDLINE', 'SHORT', 'SINK', 'PROCESS'];
const VISION_IMAGE_VERSION = 2;   // VisionInspectionNew 와 같은 값 (nginx 1년 캐시 우회)

const pad2 = (n) => String(n).padStart(2, '0');
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const timeOf = (iso) => {
  const m = iso ? String(iso).match(/T(\d{2}:\d{2}:\d{2})/) : null;
  return m ? m[1] : '-';
};
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const imageSrc = (file) => (file ? `/vision/${file}?v=${VISION_IMAGE_VERSION}` : null);

export function LabelBadge({ label, type, tr }) {
  if (label !== 'OK' && label !== 'NG') return <span className="al-muted">—</span>;
  return (
    <span className="al-label">
      <span className={`al-badge ${label === 'NG' ? 'al-badge-ng' : 'al-badge-ok'}`}>{tr(label === 'NG' ? 'ng' : 'ok')}</span>
      {label === 'NG' && type && <span className={`al-type-tag al-type-${type}`}>{tr(`types.${type}`)}</span>}
    </span>
  );
}

export function ReliabilityBar({ value, threshold = 0.85 }) {
  const v = value == null ? 0 : Math.max(0, Math.min(1, Number(value)));
  return (
    <span className="al-rel">
      <span className={`al-rel-fill ${v >= threshold ? 'al-rel-hi' : 'al-rel-lo'}`} style={{ width: `${v * 100}%` }} />
      <span className="al-rel-th" style={{ left: `${threshold * 100}%` }} />
    </span>
  );
}

export function StatusTag({ status, kind, tr }) {
  const key = status === 'REVIEW' && kind === 'AUDIT' ? 'AUDIT' : (status || 'NONE');
  return <span className={`al-status al-status-${key.toLowerCase()}`}>{tr(`status.${key}`)}</span>;
}

function AutoLabelNew({ equipmentId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.autoLabel.${key}`, params), [t]);
  const navigate = useNavigate();

  const [date, setDate] = useState(todayStr());
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => { setDate(todayStr()); setStatus(''); setPage(0); setSelected(null); }, [equipmentId]);

  const load = useCallback(async (silent = false) => {
    if (!equipmentId) return;
    if (!silent) setIsLoading(true);
    try {
      const res = await fetchAutoLabels(equipmentId, { date, status, page, size: PAGE_SIZE });
      setData(res || null);
      setError(null);
    } catch (e) {
      console.error('Failed to load auto labels:', e);
      if (!silent) setError(t('pages.monitoring.machine.common.dataLoadFailed'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [equipmentId, date, status, page, t]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    if (date !== todayStr()) return undefined;
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [date, load]);
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const s = data?.summary;
  const items = data?.items || [];
  const totalItems = data?.totalItems || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const threshold = Number(s?.threshold ?? 0.85);
  const hist = s?.histogram || new Array(10).fill(0);
  const histMax = Math.max(1, ...hist.map(Number));
  const typeMax = Math.max(1, ...TYPES.map((k) => Number(s?.byType?.[k]) || 0));
  const ag = s?.agreement;
  const dsPct = s ? Math.min(100, (Number(s.datasetCount) / Math.max(1, Number(s.retrainBatchN))) * 100) : 0;

  const dateOptions = useMemo(() => {
    const set = new Set(data?.availableDates || []);
    set.add(todayStr());
    set.add(date);
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [data, date]);

  const pick = (st) => { setStatus(status === st ? '' : st); setPage(0); };

  const kpis = [
    { key: 'total', value: (s?.total ?? 0).toLocaleString(), sub: tr('ngN', { n: (s?.ng ?? 0).toLocaleString() }) },
    { key: 'auto', value: (s?.autoConfirmed ?? 0).toLocaleString(), sub: pct(s?.autoRate), tone: 'ok', filter: 'AUTO_CONFIRMED' },
    { key: 'review', value: (s?.review ?? 0).toLocaleString(), sub: tr('auditN', { n: (s?.reviewAudit ?? 0).toLocaleString() }), tone: 'warn', filter: 'REVIEW' },
    { key: 'worker', value: (s?.workerConfirmed ?? 0).toLocaleString(), sub: tr('skippedN', { n: (s?.skipped ?? 0).toLocaleString() }), tone: 'accent', filter: 'WORKER_CONFIRMED' },
    { key: 'agreement', value: pct(ag?.auditRate), sub: tr('agreementSub', { n: (ag?.auditN ?? 0).toLocaleString(), target: num(ag?.target, 0) }), tone: 'accent', filter: 'MISMATCH' },
  ];

  return (
    <div className="al-root">
      <div className="al-kpis">
        {kpis.map((k) => (
          <button key={k.key} type="button" disabled={!k.filter} onClick={k.filter ? () => pick(k.filter) : undefined}
            className={`al-kpi al-kpi-${k.tone || 'neutral'} ${k.filter ? 'al-kpi-click' : ''} ${k.filter && status === k.filter ? 'active' : ''}`}
            title={tr(`kpiHint.${k.key}`)}>
            <span className="al-kpi-label">{tr(`kpi.${k.key}`)}</span>
            <span className="al-kpi-value">{k.value}</span>
            <span className="al-kpi-sub">{k.sub}</span>
          </button>
        ))}
        <div className="al-kpi al-kpi-dataset" title={tr('kpiHint.dataset')}>
          <span className="al-kpi-label">{tr('kpi.dataset')}</span>
          <span className="al-kpi-value">{(s?.datasetCount ?? 0).toLocaleString()}<small> / {(s?.retrainBatchN ?? 1000).toLocaleString()}</small></span>
          <span className="al-progress"><span style={{ width: `${dsPct}%` }} /></span>
        </div>
      </div>

      <div className="al-top">
        <section className="al-panel">
          <div className="al-panel-head">
            <span className="al-panel-title">{tr('histTitle')}</span>
            <span className="al-panel-sub">{tr('histSub', { th: num(threshold) })}</span>
          </div>
          <div className="al-hist">
            {hist.map((n, i) => (
              <div key={i} className="al-hist-col" title={`${(i / 10).toFixed(1)}–${((i + 1) / 10).toFixed(1)}: ${n}`}>
                <span className="al-hist-n">{Number(n) > 0 ? Number(n).toLocaleString() : ''}</span>
                <span className={`al-hist-bar ${(i + 1) / 10 > threshold ? 'al-hist-hi' : 'al-hist-lo'}`} style={{ height: `${(Number(n) / histMax) * 100}%` }} />
              </div>
            ))}
            <span className="al-hist-th" style={{ left: `${threshold * 100}%` }} />
          </div>
          <div className="al-hist-axis"><span>0</span><span>0.5</span><span>1.0</span></div>
          <div className="al-legend">
            <span><i className="al-dot al-dot-lo" />{tr('legendReview')}</span>
            <span><i className="al-dot al-dot-hi" />{tr('legendAuto')}</span>
          </div>
        </section>

        <section className="al-panel">
          <div className="al-panel-head">
            <span className="al-panel-title">{tr('typeTitle')}</span>
            <span className="al-panel-sub">{tr('ngN', { n: (s?.ng ?? 0).toLocaleString() })}</span>
          </div>
          {TYPES.map((k) => {
            const n = Number(s?.byType?.[k]) || 0;
            return (
              <div key={k} className="al-type-row">
                <span className={`al-type-dot al-type-${k}`} />
                <span className="al-type-name">{tr(`types.${k}`)}</span>
                <span className="al-type-bar"><span className={`al-type-fill al-type-${k}`} style={{ width: `${(n / typeMax) * 100}%` }} /></span>
                <span className="al-type-count">{n.toLocaleString()}</span>
              </div>
            );
          })}
          <div className="al-note">{tr('ruleNote')}</div>
        </section>

        <section className="al-panel">
          <div className="al-panel-head">
            <span className="al-panel-title">{tr('flowTitle')}</span>
          </div>
          <ol className="al-flow">
            {['inspect', 'label', 'verify', 'route', 'dataset'].map((k) => <li key={k}>{tr(`flow.${k}`)}</li>)}
          </ol>
          <button type="button" className="al-link" onClick={() => navigate('/label-review')}>
            <span className="material-symbols-outlined">fact_check</span>{tr('openReview', { n: (s?.review ?? 0).toLocaleString() })}
          </button>
        </section>
      </div>

      <div className="al-filter-row">
        <span className="al-filter-label">{tr('date')}</span>
        <div className="al-select-wrap">
          <select className="al-select" value={date} onChange={(e) => { setDate(e.target.value); setPage(0); }}>
            {dateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <span className="material-symbols-outlined al-select-icon">expand_more</span>
        </div>
        <span className="al-filter-label">{tr('statusLabel')}</span>
        <div className="al-chips">
          {STATUS_FILTERS.map((f) => (
            <button key={f || 'all'} type="button" className={`al-chip ${status === f ? 'active' : ''}`} onClick={() => { setStatus(f); setPage(0); }}>
              {tr(`filters.${f || 'all'}`)}
            </button>
          ))}
        </div>
        <span className="al-result-count">{tr('countN', { n: totalItems.toLocaleString() })}</span>
      </div>

      {isLoading && !data ? (
        <div className="al-message">{t('pages.monitoring.machine.common.loading')}</div>
      ) : error ? (
        <div className="al-message al-message-error">{error}</div>
      ) : items.length === 0 ? (
        <div className="al-message">{s?.total ? tr('emptyFiltered') : tr('empty')}</div>
      ) : (
        <div className="al-table-wrap">
          <table className="al-table">
            <thead>
              <tr>
                <th>{tr('colTime')}</th><th>{tr('colShot')}</th><th>{tr('colProcess')}</th><th>{tr('colVision')}</th>
                <th>{tr('colFusion')}</th><th>{tr('colLabel')}</th><th>{tr('colReliability')}</th><th>{tr('colReason')}</th><th>{tr('colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.shotUuid} onClick={() => setSelected(it)} className={it.status === 'REVIEW' ? 'al-row-review' : ''}>
                  <td className="al-num">{timeOf(it.shotTime)}</td>
                  <td className="al-num">#{it.shotCount ?? '-'}</td>
                  <td className="al-num"><span className={it.processVerdict === 'NG' ? 'al-c-ng' : ''}>{num(it.processScore)}</span></td>
                  <td className="al-num"><span className={it.visionVerdict === 'NG' ? 'al-c-ng' : ''}>{num(it.visionScore)}</span></td>
                  <td><span className={`al-verdict ${it.fusionVerdict === 'NG' ? 'al-c-ng' : 'al-c-ok'}`}>{tr(it.fusionVerdict === 'NG' ? 'ng' : 'ok')}</span></td>
                  <td><LabelBadge label={it.label} type={it.defectType} tr={tr} /></td>
                  <td><span className="al-cell-rel"><ReliabilityBar value={it.reliability} threshold={threshold} /><span className="al-num">{num(it.reliability)}</span></span></td>
                  <td className="al-reason">{(it.reasons || [])[0] || '—'}</td>
                  <td>
                    <StatusTag status={it.status} kind={it.reviewKind} tr={tr} />
                    {it.status === 'WORKER_CONFIRMED' && it.reviewedLabel && it.reviewedLabel !== it.label && <span className="al-mismatch">{tr('mismatch')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalItems > PAGE_SIZE && (
        <div className="al-pagination">
          <button type="button" className="al-page-nav" disabled={page === 0} onClick={() => setPage(page - 1)}>
            <span className="material-symbols-outlined">chevron_left</span>{tr('prev')}
          </button>
          <span className="al-page-info">{page + 1} / {totalPages}</span>
          <button type="button" className="al-page-nav" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
            {tr('next')}<span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      )}

      {selected && (
        <div className="al-modal-backdrop" onClick={() => setSelected(null)} role="presentation">
          <div className="al-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="al-modal-head">
              <span className="al-modal-title">{tr('detailTitle', { n: selected.shotCount ?? '-' })}</span>
              <button type="button" className="al-modal-close" onClick={() => setSelected(null)} aria-label={tr('close')}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="al-modal-body">
              <div className="al-modal-img">
                {imageSrc(selected.imageFile) ? <img src={imageSrc(selected.imageFile)} alt="" /> : <span className="al-muted">{tr('noImage')}</span>}
              </div>
              <div className="al-modal-right">
                <ReliabilityDetail item={selected} threshold={threshold} tr={tr} />
                {selected.status === 'REVIEW' && (
                  <button type="button" className="al-link" onClick={() => navigate(`/label-review?equipmentId=${equipmentId}`)}>
                    <span className="material-symbols-outlined">fact_check</span>{tr('goReview')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* 신뢰도 근거 — 라벨 검토 화면과 같이 쓴다 */
export function ReliabilityDetail({ item, threshold = 0.85, tr }) {
  const c = item.components || {};
  const rows = [
    ['c_v', tr('comp.cv'), c.c_v, `z ${num(c.z_v)}`],
    ['c_p', tr('comp.cp'), c.c_p, `z ${num(c.z_p)}`],
    ['m_f', tr('comp.mf'), c.m_f, null],
    ['sync', tr('comp.sync'), c.sync, item.syncConfidence != null ? num(item.syncConfidence) : null],
    ['trend', tr('comp.trend'), c.trend, null],
  ];
  return (
    <div className="al-detail">
      <div className="al-detail-head">
        <LabelBadge label={item.label} type={item.defectType} tr={tr} />
        <StatusTag status={item.status} kind={item.reviewKind} tr={tr} />
      </div>
      <div className="al-detail-rel">
        <span className="al-detail-rel-label">{tr('colReliability')}</span>
        <ReliabilityBar value={item.reliability} threshold={threshold} />
        <b className="al-num">{num(item.reliability, 3)}</b>
        <span className="al-muted">{Number(item.reliability) >= threshold ? '≥' : '<'} {num(threshold)}</span>
      </div>
      <table className="al-comp">
        <tbody>
          {rows.map(([k, name, v, extra]) => (
            <tr key={k}>
              <td>{name}</td>
              <td><span className="al-comp-bar"><span style={{ width: `${Math.max(0, Math.min(1, Number(v) || 0)) * 100}%` }} /></span></td>
              <td className="al-num">{num(v)}</td>
              <td className="al-muted al-num">{extra || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="al-formula">{tr('formula')}</div>
      <div className="al-scores">
        <span>{tr('colProcess')} <b className={item.processVerdict === 'NG' ? 'al-c-ng' : ''}>{num(item.processScore, 3)}</b></span>
        <span>{tr('colVision')} <b className={item.visionVerdict === 'NG' ? 'al-c-ng' : ''}>{num(item.visionScore, 3)}</b>
          {item.visionVerdict === 'NG' && item.visionDefectType ? ` (${tr(`types.${item.visionDefectType}`)})` : ''}</span>
        <span>{tr('colFusion')} <b className={item.fusionVerdict === 'NG' ? 'al-c-ng' : ''}>{num(item.fusionScore, 3)}</b></span>
      </div>
      {(item.reasons || []).length > 0 && (
        <ul className="al-reasons">{item.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      )}
      {item.reviewedAt && (
        <div className="al-note">
          {tr('reviewedBy', { label: item.status === 'SKIPPED' ? tr('status.SKIPPED')
              : item.reviewedType ? tr(`types.${item.reviewedType}`) : tr(item.reviewedLabel === 'NG' ? 'ng' : 'ok'),
            by: item.reviewedBy || '-', at: String(item.reviewedAt).replace('T', ' ').slice(0, 16) })}
        </div>
      )}
    </div>
  );
}

export default AutoLabelNew;
